import { Star } from "lucide-react";
import { trackEvent } from "@/lib/tracking";

interface GoogleReviewsBadgeProps {
  rating?: number;
  count?: number;
  url?: string;
  compact?: boolean;
  /** Visual tone — use "dark" when placing on a dark surface (e.g. footer). */
  tone?: "light" | "dark";
}

export function GoogleReviewsBadge({
  rating = 4.4,
  count = 116,
  url = "https://share.google/ex6hlV5y3QnHwzg2P",
  compact = false,
  tone = "light",
}: GoogleReviewsBadgeProps) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.3;
  const isDark = tone === "dark";

  if (compact) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("review_link_click", { location: "compact_badge" })}
        className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
          isDark ? "text-white hover:text-primary" : "text-foreground hover:text-primary"
        }`}
      >
        <Star className="h-4 w-4 fill-[#FBBC04] text-[#FBBC04]" />
        <span className="font-bold">{rating}</span>
        <span className={isDark ? "text-white/80" : "text-muted-foreground"}>({count} reviews)</span>
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent("review_link_click", { location: "full_badge" })}
      className="inline-flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-3.5 hover:border-primary/30 hover:shadow-md transition-all group"
    >
      {/* Google G icon approximation */}
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-background border border-border">
        <span className="text-lg font-black text-foreground">G</span>
      </div>
      <div className="text-left">
        <div className="flex items-center gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={`h-3.5 w-3.5 ${
                i < fullStars
                  ? "fill-[#FBBC04] text-[#FBBC04]"
                  : i === fullStars && hasHalf
                    ? "fill-[#FBBC04]/50 text-[#FBBC04]"
                    : "fill-muted text-muted"
              }`}
            />
          ))}
          <span className="ml-1 text-sm font-bold text-foreground">{rating}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {count} reviews on <span className="font-semibold text-foreground">Google</span>
        </p>
      </div>
    </a>
  );
}
